// CoveragePanel — "Who is paying for this admission?" (Insurance Claims
// Phase 4.5.1, wireframe S1: docs/design/insurance-claims/
// Claims_Desk_Screens_Wireframe.html#s1). Used in two places:
//   - IPStage.tsx, as the step shown right after an admission is created
//     (coverage needs an admission_id — it can't be captured before one
//     exists), with a "Save & continue" / "Skip (self-pay)" pair.
//   - IPModule.tsx's AdmissionDetail, to view/edit an already-admitted
//     patient's coverage (reached from the "💳 Coverage" summary card).
//
// `category` (self/cashless/reimbursement/scheme/corporate) is a UI-only
// grouping over `scheme_code` — the backend only has `scheme_code`. Two of
// the five categories need something `scheme_code` alone can't express:
//   - "scheme" covers four scheme_codes (pmjay/cmchis/cghs/esi), so a second
//     picker appears once it's chosen.
//   - "reimbursement" and "cashless" are BOTH scheme_code='insurance' — the
//     schema has no column for "cashless vs reimbursement" on the coverage
//     row itself; that distinction lives only on `claims.mode`, and
//     utils/claimAutoCreate.js's ensureClaimForCoverage (what POST .../payers
//     triggers) always derives 'cashless' for scheme_code='insurance' once a
//     payer is resolved. So "reimbursement" deliberately leaves
//     insurer_payer_id/tpa_payer_id unresolved on the coverage row (no
//     auto-claim opens) and instead creates the claim explicitly via
//     `POST /api/claims` with `mode:'reimbursement'` — the insurer is still
//     recorded, just as free text on `insurer_or_tpa` rather than the FK,
//     which is an accepted, documented limitation (see the design doc's own
//     note on this) rather than a workaround to "fix" later without a schema
//     change.
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Section } from '@/components/ui/Section';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { getPayers } from '@/features/admin/api';
import type { Payer } from '@/features/admin/types';
import {
  usePayerSchemes, useAdmissionPayers, useCreateCoverage, useUpdateCoverage, useVerifyCoverageId, useCreateClaim,
  useAdmissionSummary, useUsersMap,
} from './hooks';
import { DocumentUploadField } from './DocumentUploadField';
import MobileUploadQRModal from '@/components/MobileUploadQRModal';
import { openPatientDocument } from '@/api';
import { GOVT_SCHEME_CODES, PAYER_SCHEME_SELF, type Coverage, type CoverageCategory, type CoverageInput, type IdProofType, type PolicyType } from './coverageTypes';

const ADMISSION_TYPE_LABEL: Record<string, string> = {
  emergency: 'Emergency', elective: 'Elective', 'transfer-in': 'Transfer-in', 'day-care': 'Day-care',
};

const CATEGORY_OPTIONS: Array<{ value: CoverageCategory; label: string }> = [
  { value: 'self', label: 'Self-pay' },
  { value: 'cashless', label: 'Insurance: cashless' },
  { value: 'reimbursement', label: 'Insurance: reimbursement' },
  { value: 'scheme', label: 'Govt scheme (PM-JAY / CMCHIS)' },
  { value: 'corporate', label: 'Corporate credit' },
];

const POLICY_TYPES: PolicyType[] = ['individual', 'floater', 'group', 'scheme'];
const ID_PROOF_TYPES: IdProofType[] = ['aadhaar', 'voter', 'dl', 'passport', 'other'];

function categoryForCoverage(c: Coverage | null, claimMode?: string | null): CoverageCategory {
  if (!c || c.scheme_code === PAYER_SCHEME_SELF) return 'self';
  if (c.scheme_code === 'corporate') return 'corporate';
  if ((GOVT_SCHEME_CODES as readonly string[]).includes(c.scheme_code)) return 'scheme';
  if (c.scheme_code === 'insurance') return claimMode === 'reimbursement' ? 'reimbursement' : 'cashless';
  return 'cashless';
}

export interface CoveragePanelProps {
  admissionId: number;
  /** The claim mode for this admission's coverage, if a claim already
   * exists (undefined when none has been looked up/created yet) — used only
   * to tell a cashless coverage apart from a reimbursement one on re-open,
   * since scheme_code alone can't (see the file header). */
  existingClaimMode?: string | null;
  /** Shown after the primary action instead of "Save" — e.g. "Save & continue →". */
  continueLabel?: string;
  onSaved?: (coverage: Coverage, claimId: number | null) => void;
  /** Self-pay needs no coverage row at all — offered as a secondary action
   * next to Save, matching wireframe S1's own flow for the common case. */
  onSkip?: () => void;
  skipLabel?: string;
}

export function CoveragePanel({ admissionId, existingClaimMode, continueLabel = 'Save', onSaved, onSkip, skipLabel = 'Skip — self-pay' }: CoveragePanelProps) {
  const schemesQuery = usePayerSchemes();
  const coverageQuery = useAdmissionPayers(admissionId);
  // Footer context line (wireframe S1: "Admission type: Emergency (step 1) ·
  // IP No minted on admit: IP/2026-27/00412") — `ip_number`/`admission_type`
  // are real columns on the admissions row (Insurance Claims Phase 1) that
  // predate the shared `Admission` TS interface (DischargeSummaryChat.tsx),
  // so they're read off the raw response rather than widening that type for
  // two fields only this footer needs.
  const admissionSummary = useAdmissionSummary(admissionId) as {
    data?: { ip_number?: string; admission_type?: string; patient_uhid?: string } | undefined;
  };
  const usersMap = useUsersMap();
  const createMutation = useCreateCoverage(admissionId);
  const updateMutation = useUpdateCoverage(admissionId);
  const verifyMutation = useVerifyCoverageId(admissionId);
  const createClaimMutation = useCreateClaim();

  const existing = (coverageQuery.data ?? [])[0] ?? null;

  const [category, setCategory] = useState<CoverageCategory>('self');
  const [govtSchemeCode, setGovtSchemeCode] = useState<string>('pmjay');
  const [insurerPayerId, setInsurerPayerId] = useState<string>('');
  const [tpaPayerId, setTpaPayerId] = useState<string>('');
  const [insurers, setInsurers] = useState<Payer[]>([]);
  const [tpas, setTpas] = useState<Payer[]>([]);
  const [policyNo, setPolicyNo] = useState('');
  const [memberId, setMemberId] = useState('');
  const [policyType, setPolicyType] = useState<PolicyType>('individual');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [proposerName, setProposerName] = useState('');
  const [relationToProposer, setRelationToProposer] = useState('Self');
  const [employer, setEmployer] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [otherPolicyExists, setOtherPolicyExists] = useState(false);
  const [sumInsured, setSumInsured] = useState('');
  const [balanceSumInsured, setBalanceSumInsured] = useState('');
  const [roomEntitlement, setRoomEntitlement] = useState('');
  const [roomRentLimit, setRoomRentLimit] = useState('');
  const [copayPercent, setCopayPercent] = useState('0');
  const [deductible, setDeductible] = useState('0');
  const [schemeBeneficiaryId, setSchemeBeneficiaryId] = useState('');
  const [verificationRef, setVerificationRef] = useState('');
  const [idProofType, setIdProofType] = useState<IdProofType>('aadhaar');
  const [idProofLast4, setIdProofLast4] = useState('');
  // Scans (wireframe S1): the ID proof and the insurance card land in
  // patient_documents (ID Proof / Insurance Card / Policy) through the normal
  // upload or the mobile-upload QR flow, and are linked on the coverage row.
  const [idProofDocumentId, setIdProofDocumentId] = useState<number | null>(null);
  const [cardDocumentId, setCardDocumentId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [savedResult, setSavedResult] = useState<{ coverage: Coverage; claimId: number | null } | null>(null);

  // Prefill from an existing coverage row (edit mode) once it loads.
  useEffect(() => {
    if (!existing) return;
    setCategory(categoryForCoverage(existing, existingClaimMode));
    if ((GOVT_SCHEME_CODES as readonly string[]).includes(existing.scheme_code)) setGovtSchemeCode(existing.scheme_code);
    setInsurerPayerId(existing.insurer_payer_id != null ? String(existing.insurer_payer_id) : '');
    setTpaPayerId(existing.tpa_payer_id != null ? String(existing.tpa_payer_id) : '');
    setPolicyNo(existing.policy_no ?? '');
    setMemberId(existing.member_id ?? '');
    setPolicyType((existing.policy_type as PolicyType) ?? 'individual');
    setValidFrom(existing.valid_from ?? '');
    setValidTo(existing.valid_to ?? '');
    setProposerName(existing.proposer_name ?? '');
    setRelationToProposer(existing.relation_to_proposer ?? 'Self');
    setEmployer(existing.employer ?? '');
    setEmployeeId(existing.employee_id ?? '');
    setOtherPolicyExists(!!existing.other_policy_exists);
    setSumInsured(existing.sum_insured != null ? String(existing.sum_insured) : '');
    setBalanceSumInsured(existing.balance_sum_insured != null ? String(existing.balance_sum_insured) : '');
    setRoomEntitlement(existing.room_entitlement ?? '');
    setRoomRentLimit(existing.room_rent_limit_per_day != null ? String(existing.room_rent_limit_per_day) : '');
    setCopayPercent(existing.copay_percent != null ? String(existing.copay_percent) : '0');
    setDeductible(existing.deductible != null ? String(existing.deductible) : '0');
    setSchemeBeneficiaryId(existing.scheme_beneficiary_id ?? '');
    setVerificationRef(existing.verification_ref ?? '');
    setIdProofType((existing.id_proof_type as IdProofType) ?? 'aadhaar');
    setIdProofLast4(existing.id_proof_last4 ?? '');
    setIdProofDocumentId(existing.id_proof_document_id ?? null);
    setCardDocumentId(existing.card_document_id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id]);

  useEffect(() => {
    if (category !== 'cashless' && category !== 'reimbursement') return;
    getPayers({ type: 'insurer', active: 'true' }).then(setInsurers).catch(() => setInsurers([]));
    getPayers({ type: 'tpa', active: 'true' }).then(setTpas).catch(() => setTpas([]));
  }, [category]);

  const [corporatePayers, setCorporatePayers] = useState<Payer[]>([]);
  useEffect(() => {
    if (category !== 'corporate') return;
    getPayers({ type: 'corporate', active: 'true' }).then(setCorporatePayers).catch(() => setCorporatePayers([]));
  }, [category]);

  const [govtPayers, setGovtPayers] = useState<Payer[]>([]);
  useEffect(() => {
    if (category !== 'scheme') return;
    getPayers({ type: 'govt_scheme', active: 'true' }).then(setGovtPayers).catch(() => setGovtPayers([]));
  }, [category]);

  const schemeCodeForSave = useMemo(() => {
    if (category === 'self') return PAYER_SCHEME_SELF;
    if (category === 'cashless' || category === 'reimbursement') return 'insurance';
    if (category === 'corporate') return 'corporate';
    return govtSchemeCode;
  }, [category, govtSchemeCode]);

  const selectedInsurer = insurers.find((p) => String(p.id) === insurerPayerId) || null;
  const selectedGovtPayer = govtPayers.find((p) => String(p.id) === insurerPayerId) || null;
  const selectedCorporatePayer = corporatePayers.find((p) => String(p.id) === insurerPayerId) || null;

  const saving = createMutation.isPending || updateMutation.isPending || createClaimMutation.isPending;

  const save = async () => {
    setError('');
    if (idProofLast4 && !/^[0-9A-Za-z]{4}$/.test(idProofLast4)) {
      setError('ID proof — enter exactly the last 4 characters, never the full number.');
      return;
    }
    const input: Record<string, unknown> = {
      scheme_code: schemeCodeForSave,
      policy_no: policyNo || null,
      member_id: memberId || null,
      valid_from: validFrom || null,
      valid_to: validTo || null,
      id_proof_type: idProofLast4 ? idProofType : null,
      id_proof_last4: idProofLast4 || null,
      id_proof_document_id: idProofDocumentId,
      card_document_id: category === 'self' ? null : cardDocumentId,
    };
    if (category === 'cashless') {
      input.insurer_payer_id = insurerPayerId ? Number(insurerPayerId) : null;
      input.tpa_payer_id = tpaPayerId ? Number(tpaPayerId) : null;
      input.insurer_or_tpa = selectedInsurer?.name ?? null;
      input.policy_type = policyType;
      input.proposer_name = proposerName || null;
      input.relation_to_proposer = relationToProposer || null;
      input.other_policy_exists = otherPolicyExists;
      input.sum_insured = sumInsured ? Number(sumInsured) : null;
      input.balance_sum_insured = balanceSumInsured ? Number(balanceSumInsured) : null;
      input.room_entitlement = roomEntitlement || null;
      input.room_rent_limit_per_day = roomRentLimit ? Number(roomRentLimit) : null;
      input.copay_percent = copayPercent ? Number(copayPercent) : 0;
      input.deductible = deductible ? Number(deductible) : 0;
    } else if (category === 'reimbursement') {
      // Deliberately no insurer_payer_id/tpa_payer_id here — see the file
      // header. The chosen insurer is recorded as free text only.
      input.insurer_or_tpa = selectedInsurer?.name ?? null;
      input.policy_type = policyType;
      input.proposer_name = proposerName || null;
      input.relation_to_proposer = relationToProposer || null;
      input.other_policy_exists = otherPolicyExists;
      input.sum_insured = sumInsured ? Number(sumInsured) : null;
    } else if (category === 'scheme') {
      input.insurer_payer_id = insurerPayerId ? Number(insurerPayerId) : null;
      input.insurer_or_tpa = selectedGovtPayer?.name ?? null;
      input.scheme_beneficiary_id = schemeBeneficiaryId || null;
      input.verification_ref = verificationRef || null;
    } else if (category === 'corporate') {
      input.insurer_payer_id = insurerPayerId ? Number(insurerPayerId) : null;
      input.insurer_or_tpa = selectedCorporatePayer?.name ?? null;
      input.employer = employer || selectedCorporatePayer?.name || null;
      input.employee_id = employeeId || null;
    }

    try {
      const result = existing
        ? await updateMutation.mutateAsync({ coverageId: existing.id, input })
        : await createMutation.mutateAsync(input as unknown as CoverageInput);

      let claimId = result.claim_id ?? null;
      // The one case ensureClaimForCoverage can never open on its own.
      if (category === 'reimbursement' && !claimId && insurerPayerId) {
        const claim = await createClaimMutation.mutateAsync({
          admission_id: admissionId, payer_id: Number(insurerPayerId), mode: 'reimbursement',
        });
        claimId = claim.id;
      }
      setSavedResult({ coverage: result, claimId });
      onSaved?.(result, claimId);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    }
  };

  const verify = async () => {
    if (!existing) return;
    try { await verifyMutation.mutateAsync(existing.id); }
    catch (e) { setError(e instanceof ApiError ? e.message : String(e)); }
  };

  if (coverageQuery.isLoading || schemesQuery.isLoading) {
    return <Section title="Who is paying for this admission?"><LoadingState label="Loading coverage…" /></Section>;
  }
  if (coverageQuery.isError) {
    return (
      <Section title="Who is paying for this admission?">
        <ErrorState message={coverageQuery.error instanceof ApiError ? coverageQuery.error.message : 'Could not load coverage'} onRetry={() => coverageQuery.refetch()} />
      </Section>
    );
  }

  // The on-save checklist (wireframe S1's own "On save" card) — a fixed
  // explainer of what saving DOES before it's been done, swapped for the
  // real result (the actual claim ref, if one opened) once it has.
  const onSaveChecklist = savedResult ? (
    <div className="rounded-[var(--radius)] bg-success-soft px-3 py-2 text-[12px] text-success-ink">
      ✔ Coverage row saved (<code>ip_admission_payers</code>)
      {savedResult.claimId != null && <><br />✔ Claim opened and assigned to the Claims desk queue</>}
      <br />✔ Bed-board card shows the resulting payer chip
    </div>
  ) : category !== 'self' ? (
    <div className="rounded-[var(--radius)] border border-dashed border-border px-3 py-2 text-[12px] text-ink-muted">
      On save: ✔ a coverage row is recorded · ✔ a claim is opened (once an insurer is resolved) and queued for the
      Claims desk · ✔ the bed-board card shows the resulting payer chip.
    </div>
  ) : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[15px] font-semibold">Who is paying for this admission?</h2>
      </div>
      {/* The shared joined track caps itself at 420px (sized for short scales
          like Low→Critical); five long payer labels at ~84px each overflowed
          ("reimbursement" spilled into its neighbour). This control gets the
          card's width instead, so each label fits on one or two lines. Below
          36rem of panel width even that overlaps, so the choices stack as a
          list — a container query, not a viewport breakpoint, because the
          panel is narrow on a tablet with the sidebar open too. */}
      <div className="@container">
        <SegmentedControl
          options={CATEGORY_OPTIONS} value={category} onChange={setCategory}
          className={
            'max-w-[860px] [&>*]:[overflow-wrap:anywhere] ' +
            '@max-xl:max-w-none @max-xl:flex-col @max-xl:[&>*+*]:border-l-0 @max-xl:[&>*+*]:border-t ' +
            '@max-xl:[&>*]:min-h-11 @max-xl:[&>*]:justify-start @max-xl:[&>*]:px-4 @max-xl:[&>*]:text-left'
          }
        />
      </div>

        {category === 'self' && (
          <div className="rounded-[var(--radius)] border border-border bg-muted px-3 py-2 text-[12.5px] text-ink-muted">
            No policy/claim record is created for a self-pay admission. Switch segments above if that changes.
          </div>
        )}

        {category === 'scheme' && (
          <div className="rounded-[var(--radius)] bg-info-soft px-3 py-2 text-[12px] text-info-ink">
            Zero billing: no charges may be collected from this patient for package services.
          </div>
        )}

        {/* wireframe S1's own 7:3 split: policy/scheme/corporate particulars on
            the left, identity check + the "on save" explainer on the right —
            self-pay has neither column, since there's nothing to capture. */}
        {category !== 'self' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[7fr_3fr]">
            <Section title={
              category === 'scheme' ? 'Scheme' : category === 'corporate' ? 'Corporate credit' : 'Policy & card'
            }>
              {(category === 'cashless' || category === 'reimbursement') && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <FormField label="Insurer" required>
                    <NativeSelect value={insurerPayerId} onChange={(e) => setInsurerPayerId(e.target.value)}>
                      <option value="">Select insurer…</option>
                      {insurers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </NativeSelect>
                  </FormField>
                  {category === 'cashless' && (
                    <FormField label="TPA (auto-suggested from insurer mapping)">
                      <NativeSelect value={tpaPayerId} onChange={(e) => setTpaPayerId(e.target.value)}>
                        <option value="">None / self-administered</option>
                        {tpas.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </NativeSelect>
                    </FormField>
                  )}
                  <FormField label="Policy no." required={category === 'cashless'}>
                    <Input value={policyNo} onChange={(e) => setPolicyNo(e.target.value)} />
                  </FormField>
                  <FormField label="Member / card ID" required={category === 'cashless'}>
                    <Input value={memberId} onChange={(e) => setMemberId(e.target.value)} />
                  </FormField>
                  <FormField label="Policy type">
                    <NativeSelect value={policyType} onChange={(e) => setPolicyType(e.target.value as PolicyType)}>
                      {POLICY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </NativeSelect>
                  </FormField>
                  <FormField label="Valid">
                    <div className="flex items-center gap-1.5">
                      <Input type="date" className="min-w-0 flex-1" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
                      <span className="text-ink-muted">→</span>
                      <Input type="date" className="min-w-0 flex-1" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
                    </div>
                  </FormField>
                  <FormField label="Proposer / primary insured">
                    <Input value={proposerName} onChange={(e) => setProposerName(e.target.value)} />
                  </FormField>
                  <FormField label="Relation to proposer">
                    <Input value={relationToProposer} onChange={(e) => setRelationToProposer(e.target.value)} placeholder="Self" />
                  </FormField>
                  <FormField label="Other policy?" className="flex items-end">
                    <label className="flex items-center gap-2 pb-2">
                      <Checkbox checked={otherPolicyExists} onCheckedChange={(v) => setOtherPolicyExists(v === true)} />
                      <span className="text-[12.5px]">Patient holds another policy</span>
                    </label>
                  </FormField>

                  {category === 'cashless' && (
                    <>
                      <div className="col-span-full mt-1 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                        Declared limits (used by the estimator only; editable later)
                      </div>
                      <FormField label="Sum insured ₹"><Input type="number" min={0} value={sumInsured} onChange={(e) => setSumInsured(e.target.value)} /></FormField>
                      <FormField label="Balance SI (as told by patient/TPA) ₹"><Input type="number" min={0} value={balanceSumInsured} onChange={(e) => setBalanceSumInsured(e.target.value)} /></FormField>
                      <FormField label="Room entitlement"><Input value={roomEntitlement} onChange={(e) => setRoomEntitlement(e.target.value)} placeholder="Single private" /></FormField>
                      <FormField label="Room rent cap / day ₹"><Input type="number" min={0} value={roomRentLimit} onChange={(e) => setRoomRentLimit(e.target.value)} /></FormField>
                      <FormField label="Co-pay %"><Input type="number" min={0} max={100} value={copayPercent} onChange={(e) => setCopayPercent(e.target.value)} /></FormField>
                      <FormField label="Deductible ₹"><Input type="number" min={0} value={deductible} onChange={(e) => setDeductible(e.target.value)} /></FormField>
                    </>
                  )}
                  {category === 'reimbursement' && (
                    <div className="col-span-full rounded-[var(--radius)] bg-muted px-3 py-2 text-[12px] text-ink-muted">
                      Reimbursement: the patient pays in full, like self-pay, and this record only tracks document
                      issue for their own claim — it never gates discharge or splits the bill.
                    </div>
                  )}
                </div>
              )}

              {category === 'scheme' && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <FormField label="Scheme">
                    <NativeSelect value={govtSchemeCode} onChange={(e) => setGovtSchemeCode(e.target.value)}>
                      {GOVT_SCHEME_CODES.map((code) => {
                        const label = schemesQuery.data?.find((s) => s.code === code)?.label ?? code;
                        return <option key={code} value={code}>{label}</option>;
                      })}
                    </NativeSelect>
                  </FormField>
                  <FormField label="Scheme organisation" required>
                    <NativeSelect value={insurerPayerId} onChange={(e) => setInsurerPayerId(e.target.value)}>
                      <option value="">Select…</option>
                      {govtPayers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </NativeSelect>
                  </FormField>
                  <FormField label="Beneficiary ID" required>
                    <Input value={schemeBeneficiaryId} onChange={(e) => setSchemeBeneficiaryId(e.target.value)} />
                  </FormField>
                  <FormField label="Verification reference (from the scheme portal / BIS / e-KYC)">
                    <Input value={verificationRef} onChange={(e) => setVerificationRef(e.target.value)} />
                  </FormField>
                </div>
              )}

              {category === 'corporate' && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <FormField label="Corporate" required>
                    <NativeSelect value={insurerPayerId} onChange={(e) => setInsurerPayerId(e.target.value)}>
                      <option value="">Select…</option>
                      {corporatePayers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </NativeSelect>
                  </FormField>
                  <FormField label="Employer (as printed on the credit letter)">
                    <Input value={employer} onChange={(e) => setEmployer(e.target.value)} />
                  </FormField>
                  <FormField label="Employee ID">
                    <Input value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} />
                  </FormField>
                </div>
              )}
            </Section>

            <Section title="Identity check">
              <div className="grid grid-cols-1 gap-3">
                <FormField label="Photo ID type">
                  <NativeSelect value={idProofType} onChange={(e) => setIdProofType(e.target.value as IdProofType)}>
                    {ID_PROOF_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </NativeSelect>
                </FormField>
                <FormField label="Last 4 digits only">
                  <Input value={idProofLast4} onChange={(e) => setIdProofLast4(e.target.value.slice(0, 4))} maxLength={4} placeholder="4417" />
                </FormField>
                <FormField label="Scans">
                  <div className="flex flex-col gap-2">
                    <ScanField
                      label="ID front/back" category="ID Proof" uhid={admissionSummary.data?.patient_uhid}
                      documentId={idProofDocumentId} onLinked={setIdProofDocumentId}
                    />
                    <ScanField
                      label={category === 'scheme' ? 'Scheme card' : category === 'corporate' ? 'Credit letter / ID card' : 'Insurance card'}
                      category="Insurance Card / Policy" uhid={admissionSummary.data?.patient_uhid}
                      documentId={cardDocumentId} onLinked={setCardDocumentId}
                    />
                  </div>
                </FormField>
                <div>
                  {existing?.id_verified_at ? (
                    <Badge variant="success">
                      ☑ {existing.id_verified_by_user_id != null ? `${usersMap.get(existing.id_verified_by_user_id) ?? 'Verified'} · ` : 'Verified · '}
                      {new Date(existing.id_verified_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </Badge>
                  ) : existing ? (
                    <Button size="sm" variant="ghost" loading={verifyMutation.isPending} disabled={!existing.id_proof_type || !existing.id_proof_last4} onClick={verify}>
                      Mark as verified
                    </Button>
                  ) : (
                    <span className="text-[11px] text-ink-muted">Save first, then verify.</span>
                  )}
                </div>
              </div>
              <div className="mt-1 text-[11px] text-ink-muted">
                Full government ID is never stored — only the last 4 characters. Scans are filed in the patient's
                documents (ID Proof, Insurance Card) and linked here; remember to save after attaching.
              </div>
              <div className="mt-3">
                {onSaveChecklist}
              </div>
            </Section>
          </div>
        )}

        {error && <div className="text-[12px] text-danger-ink">{error}</div>}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button loading={saving} onClick={save}>{category === 'self' ? 'Save' : continueLabel}</Button>
            {category === 'self' && onSkip && (
              <Button variant="ghost" onClick={onSkip}>{skipLabel}</Button>
            )}
          </div>
          {admissionSummary.data && (
            <div className="text-[11px] text-ink-muted">
              {admissionSummary.data.admission_type && (
                <>Admission type: <b>{ADMISSION_TYPE_LABEL[admissionSummary.data.admission_type] ?? admissionSummary.data.admission_type}</b> · </>
              )}
              {admissionSummary.data.ip_number && <>IP No: <b>{admissionSummary.data.ip_number}</b></>}
            </div>
          )}
        </div>
    </div>
  );
}

/** One scan slot (wireframe S1's "📎 ID front/back · 📎 Insurance card · 📱
 * Scan with phone"): attach from this computer, or open the mobile-upload QR
 * and link whatever the phone sends (the modal reports the uploaded
 * documents, newest first). Linked here; persisted on Save. */
function ScanField({ label, category, uhid, documentId, onLinked }: {
  label: string; category: string; uhid: string | undefined; documentId: number | null; onLinked: (id: number) => void;
}) {
  const [phoneOpen, setPhoneOpen] = useState(false);
  if (!uhid) return <div className="text-[12px] text-ink-muted">{label}: available once the admission has loaded.</div>;
  return (
    <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
      <span className="min-w-[96px]">{documentId ? '📎' : '○'} {label}</span>
      {documentId != null && (
        <button type="button" className="text-[12px] text-link underline" onClick={() => openPatientDocument(uhid, documentId)}>View</button>
      )}
      <DocumentUploadField uhid={uhid} category={category} uploadedFileName={documentId ? 'attached' : null} onUploaded={onLinked} />
      <Button type="button" size="sm" variant="ghost" onClick={() => setPhoneOpen(true)}>📱 Scan with phone</Button>
      {phoneOpen && (
        <MobileUploadQRModal
          uhid={uhid} category={category} title={`Scan: ${label}`}
          onClose={() => setPhoneOpen(false)}
          onReceived={(status) => {
            const newest = status.documents[0];
            if (newest) { onLinked(newest.id); setPhoneOpen(false); }
          }}
        />
      )}
    </div>
  );
}
